import { MODALITY_CODES } from '@/lib/modality/modalityCodes';
/**
 * Shared worklist display helpers. Extracted from WorklistTable so the
 * Link tier's send-a-case table renders identically to the paid worklist —
 * one definition of the modality palette, the modality filter and
 * DICOM-name/ID formatting keeps the two surfaces from drifting apart.
 * (ModalityBadge lives in its own file so component and helper exports don't
 * mix — react-refresh constraint.)
 */
// Modality badge colors. A code not listed renders gray (ModalityBadge).
export const MODALITY_COLORS = {
    MR: 'bg-blue-600 text-white',
    CT: 'bg-teal-600 text-white',
    DX: 'bg-yellow-600 text-white',
    CR: 'bg-orange-600 text-white',
    US: 'bg-purple-600 text-white',
    XA: 'bg-pink-600 text-white',
    DR: 'bg-green-600 text-white',
    IO: 'bg-amber-700 text-white',
    PX: 'bg-lime-700 text-white',
    RF: 'bg-rose-600 text-white',
    NM: 'bg-emerald-700 text-white',
    PT: 'bg-indigo-600 text-white',
    MG: 'bg-fuchsia-600 text-white',
};
/**
 * The modality filter on the studies list and the Link send-a-case list. `value` is the token sent
 * as `modality`, which the backend splits on commas into `modality = ANY(...)`; `label` is what the
 * pill shows, the DICOM code itself, matching the badge in the row. CR/DX leads because the two are
 * one 2D x-ray workflow from where a reader sits; each stays on its own for a practice that wants
 * just one. The rest is the app-wide list, so these filters offer every modality the schedule and
 * the teleconsultation worklist do.
 */
export const MODALITY_FILTER_OPTIONS = [
    { value: 'CR,DX', label: 'CR/DX' },
    ...MODALITY_CODES.map((code) => ({ value: code, label: code })),
];
/**
 * DICOM PN display only: 'KRITZER^WINSTON' → 'KRITZER, WINSTON'.
 *
 * Case is left exactly as it was entered at acquisition. An earlier version
 * title-cased and reordered to "Given Family", which turned "MASSEY^DIRECT
 * FLIGHT (SANTIAGO)" into "Direct flight (santiago) Massey" — and, in a
 * truncating column, into "Direct flight (santiago…" with the surname gone.
 * Family-first also matches how names without a caret arrive from the backend
 * ("ZALUCKYJ, MISHCHA"), so the two forms read the same in one list.
 */
export function formatPatientNameDisplay(raw, fallback = 'Unknown') {
    if (!raw || !raw.includes('^'))
        return raw?.trim() || fallback;
    const parts = raw.split('^').flatMap((p) => {
        const v = p.trim();
        return v ? [v] : [];
    });
    if (parts.length === 0)
        return fallback;
    if (parts.length === 1)
        return parts[0];
    const family = parts[0];
    const given = parts.slice(1).join(' ');
    return `${family}, ${given}`;
}
/**
 * Sentinel patient IDs some modalities emit when nothing was entered at the
 * console. They are not identities: the backend's prior-studies / related-
 * studies / prior-reports matching treats them as NULL (PatientIdentitySql.
 * PlaceholderValues), and every list cell renders "Not recorded" instead of
 * dressing the word up as a real ID. Keep the two lists in step.
 */
const PLACEHOLDER_PATIENT_IDS = new Set([
    'UNKNOWN',
    'ANONYMOUS',
    'N/A',
    'NONE',
]);
/** True for blank IDs and for the placeholder sentinels, in any case/whitespace. */
export function isPlaceholderPatientId(raw) {
    const v = raw?.trim().toUpperCase();
    return !v || PLACEHOLDER_PATIENT_IDS.has(v);
}
export function formatPatientIdDisplay(raw, maxLength = 12) {
    const normalized = raw?.trim();
    if (!normalized)
        return '—';
    if (normalized.length <= maxLength)
        return normalized;
    return `${normalized.slice(0, 6)}…${normalized.slice(-4)}`;
}
/** Priority at and above which a study is STAT (red rule + pulsing badge). */
const STAT_PRIORITY = 7;
/** Priority at and above which (below STAT) a study is urgent (amber rule). */
const URGENT_PRIORITY = 4;
export function studyRowDisplay(study, unknownName, clinicName) {
    const patientId = study.patient.id?.trim();
    return {
        isStat: study.priority >= STAT_PRIORITY,
        isUrgent: study.priority >= URGENT_PRIORITY && study.priority < STAT_PRIORITY,
        patientId,
        patientIdRecorded: !isPlaceholderPatientId(patientId),
        patientIdDisplay: formatPatientIdDisplay(patientId),
        patientName: formatPatientNameDisplay(study.patient.name, unknownName),
        // A shared-in study shows under the caller's own clinic (the one it was
        // shared with), whatever the study's own institution says: that field can
        // carry the owner's name. The "Shared from" badge names the owner.
        institutionLabel: study.accessVia
            ? (study.accessVia.viaClinicName?.trim() ?? '')
            : study.institutionName?.trim() || clinicName?.trim() || study.clinicCode || '',
    };
}
/** The codes the badge shows: the series' modalities when known, else the study's. */
export function badgeModalities(modality, modalities) {
    const fromSeries = (modalities ?? [])
        .map((m) => m.trim().toUpperCase())
        .filter((m, i, all) => m && all.indexOf(m) === i);
    if (fromSeries.length > 0)
        return fromSeries;
    const own = modality?.trim();
    return own ? [own] : [];
}
